import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { raceData } from '@/data/races';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Map,
  BarChart2,
  ListChecks,
  Clock,
  Award,
  Mountain,
  TrendingUp,
  Zap,
  Droplet,      // air
  Utensils      // makan
} from 'lucide-react';

const REGISTER_URL = 'https://sobat.id/events/b-ultra';

const RaceDetailPage = () => {
  const { raceId } = useParams();
  const race = raceData.find((r) => r.id === raceId);

  if (!race) {
    return <Navigate to="/" />;
  }

  // ✅ ambil WS aman (kalau field belum ada, nggak error)
  const wsValue =
    race.ws ??
    race.waterStations ??
    race.water_station ??
    race.wsCount ??
    '-';

  const stats = [
    { icon: Zap, label: 'Jarak', value: race.distance },
    { icon: TrendingUp, label: 'Elevasi', value: race.elevation },
    { icon: Clock, label: 'Batas Waktu', value: race.cutOffTime },
    { icon: Award, label: 'Poin ITRA', value: race.itraPoints },
    { icon: Droplet, label: 'WS', value: wsValue }, // ✅ tambah WS
  ];

  return (
    <>
      <Helmet>
        <title>{race.name} - Banyumas Ultra</title>
        <meta name="description" content={race.longDescription} />
      </Helmet>

      <div className="bg-background text-foreground">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative h-[60vh] bg-cover bg-center"
          style={{ backgroundImage: `url(${race.heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-16">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            >
              <Link to="/races" className="flex items-center gap-2 text-primary mb-4 hover:underline">
                <ArrowLeft size={18} />
                Kembali ke semua lomba
              </Link>

              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white">
                {race.name}
              </h1>
            </motion.div>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Details */}
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
              className="lg:col-span-2"
            >
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-8">
                  <TabsTrigger value="overview">
                    <Mountain className="w-4 h-4 mr-2 hidden sm:inline-block" />
                    Ringkasan
                  </TabsTrigger>
                  <TabsTrigger value="map">
                    <Map className="w-4 h-4 mr-2 hidden sm:inline-block" />
                    Peta Rute
                  </TabsTrigger>
                  <TabsTrigger value="profile">
                    <BarChart2 className="w-4 h-4 mr-2 hidden sm:inline-block" />
                    Profil
                  </TabsTrigger>
                  <TabsTrigger value="checkpoints">
                    <ListChecks className="w-4 h-4 mr-2 hidden sm:inline-block" />
                    Pos Pemeriksaan
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="prose prose-invert max-w-none text-foreground/80">
                  <p className="lead">{race.longDescription}</p>
                  <p>
                    Tantangan ini akan membawa Anda melewati beberapa pemandangan paling ikonik di wilayah ini.
                    Bersiaplah untuk tanjakan yang menguji batas kemampuan dan turunan teknis yang membutuhkan
                    konsentrasi penuh. Setiap langkah akan menjadi bukti kekuatan dan tekad Anda.
                  </p>
                </TabsContent>

                <TabsContent value="map">
                  <div className="bg-card p-4 rounded-lg border border-border">
                    <img
                      className="w-full h-auto rounded-md object-cover"
                      alt={`Peta rute untuk ${race.name}`}
                      src="https://images.unsplash.com/1"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="profile">
                  <div className="bg-card p-4 rounded-lg border border-border">
                    <img
                      className="w-full h-auto rounded-md object-cover"
                      alt={`Profil elevasi untuk ${race.name}`}
                      src="https://images.unsplash.com/2"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="checkpoints">
                  <ul className="space-y-4">
                    {race.checkpoints.map((cp, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-between bg-card p-4 rounded-lg border border-border"
                      >
                        <div>
                          <p className="font-bold text-foreground">{cp.name}</p>
                          <p className="text-sm text-muted-foreground">{cp.type}</p>
                        </div>
                        <p className="font-mono text-primary">{cp.distance}</p>
                      </li>
                    ))}
                  </ul>
                </TabsContent>
              </Tabs>
            </motion.div>

            {/* Right Column - Stats & Register */}
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
              className="lg:col-span-1"
            >
              <div className="sticky top-24 bg-card p-8 rounded-xl border border-border shadow-lg">
                <h3 className="text-2xl font-bold text-foreground mb-6">Statistik Lomba</h3>

                {/* ✅ supaya muat 5 item (4 + WS), grid dibuat 2 kolom tetap rapi */}
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {stats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <stat.icon className="h-8 w-8 mx-auto text-primary mb-2" />
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="text-xl font-bold text-foreground">{stat.value}</p>
                    </div>
                  ))}
                </div>

                <div className="text-center mb-6">
                  <p className="text-sm text-muted-foreground">Biaya Pendaftaran</p>
                  <p className="text-4xl font-bold text-primary">{race.price}</p>
                </div>

                <Button asChild size="lg" className="w-full font-bold">
                  <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
                    Daftar Sekarang
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RaceDetailPage;